// 선생님 수업화면(활동), 학생 수업화면 진입점
import './bootstrap';
import { createApp, DefineComponent, h } from 'vue';
import { createInertiaApp, usePage } from '@inertiajs/vue3';
import PusherService from '@/app/services/PusherService';
import * as Sentry from '@sentry/vue';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';
import VueAxios from 'vue-axios';
import axios from 'axios';
import { enumMessages } from '@/lang-enum/index';
import '@/assets/css/style.scss';

import { PageProps } from './types';
import { inertiaVersionCheck, setupNavigationHandlers } from '@/utils/inertia';
import { isVueRuntimeError, isInertiaInternalError } from '@/utils/sentryFilters';
import { createI18n } from 'vue-i18n';
import messages from '@/app/lang/index';
import { textFitDirective } from './utils/textFit';
import { clickOutsideDirective } from './utils/clickOutside';
import 'froala-editor/css/froala_style.min.css';
import { initNetworkMonitoring } from './utils/networkMonitor';
import { setupTouchMode } from './utils/touchMode';
import { setupTabVisibilityRefresh } from './utils/tabVisibilityRefresh';
import { setEnumTranslator } from '@/utils/i18nEnum';

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';
const VITE_SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || null;
const VITE_SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'local';
const VITE_SENTRY_TRACES_SAMPLE_RATE = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.3;

// Inertia 초기화 타이밍에 window.__page가 비어 있을 수 있어 data-page를 fallback으로 사용
const pageElement = typeof document !== 'undefined' ? document.getElementById('app') : null;
const pageData = pageElement?.getAttribute('data-page');
const pageProps = (() => {
    if ((window as any).__page?.props) {
        return (window as any).__page.props;
    }

    if (!pageData) {
        return undefined;
    }

    try {
        return JSON.parse(pageData)?.props;
    } catch {
        return undefined;
    }
})();
const savedLocale = localStorage.getItem('app-locale');
// 선생님(teacher) 로그인인 경우에는 앱 언어를 항상 한국어로 고정
const isTeacherAuthenticated = pageProps?.auth?.guard === 'teacher';
// 가이드 모아보기 화면은 선생님,학생 구분이 없어 url로 구분
const isPresettingModulePage = window.location.pathname.startsWith('/pre-setting-modules');
/**
 *  언어 우선순위
 *  1.선생님으로 로그인하는 경우 한국어로 강제
 *  2.활동가이드 모아보기 페이지 접근시 무조건 한국어로 강제
 *  3.로컬 스토리지에 저장된 언어
 *  4.현재 요청에서 서버가 적용한 locale
 *  5.그 외 모두 한국어
 */
const initialLocale = isTeacherAuthenticated
    ? 'ko'
    : isPresettingModulePage
    ? 'ko'
    : savedLocale || pageProps?.locale || 'ko';
document.body.classList.add(`lang-${initialLocale}`);

const i18n = createI18n({
    legacy: false,
    locale: initialLocale,
    fallbackLocale: 'ko',
    fallbackWarn: false,
    missingWarn: false,
    messages: {
        ko: { ...messages.ko, enums: { ...enumMessages.ko } },
        en: { ...messages.en, enums: { ...enumMessages.en } },
        vi: { ...messages.vi, enums: { ...enumMessages.vi } },
        zh: { ...messages.zh, enums: { ...enumMessages.zh } },
    },
    warnHtmlMessage: false,
});

window.$t = i18n.global.t;

import { setI18nInstance, useSystemStoreWithOut } from '@/app/stores/system';
setI18nInstance(i18n);

// 전역에서 사용할 번역 함수 연결
setEnumTranslator(i18n.global.t.bind(i18n.global));

// PWA 설치 프롬프트 이벤트 처리
window.addEventListener('beforeinstallprompt', (e) => {
    // 기본 설치 배너가 뜨지 않도록 막음
    e.preventDefault();
    // 이벤트를 window 객체에 저장하여 모든 컴포넌트가 접근할 수 있도록 함
    window.__deferredPrompt = e;
});

// PWA 설치 완료 이벤트 처리
window.addEventListener('appinstalled', (e) => {
    window.__deferredPrompt = null;
});

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: async (name) => {
        // app 안에 수업페이지가 포함되어있어 리뉴얼한 css와 겹쳐서 비동기 scss로드처리
        // await loadPageSpecificStyles(name);
        // 페이지 컴포넌트 로드
        return resolvePageComponent(`./pages/${name}.vue`, import.meta.glob<DefineComponent>('./pages/**/*.vue'));
    },
    setup({ el, App, props, plugin }) {
        // body에 언어 클래스 설정
        const systemStore = useSystemStoreWithOut();
        // locale을 먼저 반영한 뒤 class를 갱신해야 body lang-* 클래스가 일치함
        systemStore.locale = initialLocale as any;
        systemStore.setLangClass();

        // Inertia.js 스크롤 복원 오류 방지를 위한 안전장치
        if (typeof window !== 'undefined' && !window.history.state) {
            window.history.replaceState({ scrollRegions: [] }, '', window.location.href);
        }

        const app = createApp({ render: () => h(App, props) });

        // Sentry 초기화 추가
        if (VITE_SENTRY_DSN) {
            Sentry.init({
                app,
                dsn: VITE_SENTRY_DSN,
                environment: VITE_SENTRY_ENVIRONMENT,
                tracesSampleRate: VITE_SENTRY_TRACES_SAMPLE_RATE,
                beforeSend(event) {
                    // Vue 내부 런타임 오류 필터링 (vnode/DOM 타이밍 이슈)
                    // Inertia.js 내부 오류 필터링 (history.state 접근/직렬화 이슈)
                    if (isVueRuntimeError(event) || isInertiaInternalError(event)) {
                        return null;
                    }

                    const pageData = usePage<PageProps>();
                    const user = pageData.props.auth?.user;
                    // 유저 정보 추가
                    if (user) {
                        event.user = {
                            ...event.user,
                            ...user,
                            guard: pageData.props.auth?.guard,
                        };
                    }

                    // API 실패(axios 등) 에러 필터링 - values 전체 순회
                    if (event.exception && Array.isArray(event.exception.values)) {
                        for (const exception of event.exception.values) {
                            // Cancel 에러 필터링 (요청 취소는 정상적인 동작)
                            if (
                                exception.value?.includes('요청 취소') ||
                                exception.value?.includes('Cancel') ||
                                exception.type === 'AxiosError' ||
                                exception.value?.includes('Network Error') ||
                                exception.value?.includes('Request failed with status code')
                            ) {
                                return null;
                            }
                        }
                    }
                    return event;
                },
            });
        }

        app.use(plugin);
        app.use(ZiggyVue);
        app.use(i18n);
        app.config.globalProperties.$t = i18n.global.t;

        // axios 설정
        app.use(VueAxios, axios);
        app.axios.interceptors.response.use(
            (response) => {
                inertiaVersionCheck(response);

                return Promise.resolve(response);
            },
            (error) => {
                return Promise.reject(error);
            },
        );

        app.directive('textfit', textFitDirective);
        app.directive('click-outside', clickOutsideDirective);
        app.mount(el);

        // 채널 구독
        const pageData = usePage<any>();
        if (pageData.props?.channel) {
            PusherService.getInstance().subscribeClassChannel(pageData.props.channel, pageData.props.private_channel);
        }

        initNetworkMonitoring();

        // 터치 이벤트와 마우스 이벤트를 구분하여 터치 모드 관리
        setupTouchMode();

        // 다른 탭이 활동했을 가능성이 있는 경우 stale UI 정리를 위해 새로고침
        setupTabVisibilityRefresh();

        window.addEventListener('online', () => {
            window.location.reload();
        });

        // 뒤로가기/앞으로가기 시 서버에서 데이터 재호출 처리
        setupNavigationHandlers();
    },
    progress: {
        color: '#4B5563',
    },
});
